// ─────────────────────────────────────────────────────────────────────────────
// SETTING A LOOK: CHOOSE THE ORGANISATION, SEE WHO IT REACHES, CHOOSE THE THEME.
//
// The setting belongs to the organisation, so the organisation is what you pick. An
// earlier version of this panel asked for a FACILITY and then restyled its whole chain,
// which is the route's shape leaking into the screen: `/api/facilities/:ccn/theme` is
// keyed on a building because that is what a browser has in hand, and that is an
// implementation detail, not a thing to ask a person about.
//
// ── THE TWO COUNTS THAT CAN DISAGREE, AND WHY BOTH ARE SHOWN ────────────────
//
// This screen knows the facilities THIS SESSION can see. The server counts every facility
// in the organisation. For the platform owner those are the same number; for a VP scoped
// to part of a chain they are not — and the save reaches the whole chain either way.
//
// So the list names the buildings we can name, and when the server's count is higher the
// panel says so in as many words. A list of two building names over a button that changes
// nine is the trap this panel exists to avoid, and the server's number is the true one.
//
// ── THE GATE IS A CONVENIENCE, NOT A CONTROL ────────────────────────────────
//
// The platform owner by email, or the VP role. Anything a browser can hide a browser can
// unhide; the route is what refuses, and it sits behind the facility scope check so a VP
// can only ever reach their own customer. The copy says that rather than implying the
// button is the security.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";

import ThemePicker from "./ThemePicker.jsx";

/** Who sees the control. Mirrors the modal's gate so the two cannot drift apart. */
export function canSetOrgLook(session, ownerEmails = []) {
  const email = String(session?.email || "").toLowerCase();
  return ownerEmails.map((e) => String(e).toLowerCase()).includes(email)
    || String(session?.role || "").toLowerCase() === "vp";
}

/** The ccn the theme route wants, from whatever shape the facility row is in. */
const ccnOf = (f) => f?.facility_id || f?.ccn || f?.pcc_fac_id || f?.cms_ccn || null;
const orgIdOf = (f) => f?.organization_id || f?.org_id || null;
const nameOf = (f) => f?.name || f?.facility_name || ccnOf(f) || "Unnamed facility";

/**
 * The organisation's name.
 *
 * ── A FACILITY ROW OFTEN DOES NOT CARRY ONE ─────────────────────────────────
 *
 * The first version read `organization_name || org_name` off the facility, which the
 * detail modal does because the row it holds has been joined. The rows on the Facilities
 * screen come from `facilities.select("*")` and carry `organization_id` and no name — so
 * every group fell through to the same literal and the dropdown offered "Unassigned
 * organisation" three times. Three identical labels for three different chains is worse
 * than no dropdown: it invites picking one at random.
 *
 * So the `organizations` table is consulted first (the Facilities screen already holds it),
 * then the joined fields, and only then a fallback — and the fallback shows the ID, which
 * at least distinguishes one unnamed organisation from another.
 */
function orgNameFor(f, orgsById) {
  const id = orgIdOf(f);
  const fromTable = id ? orgsById.get(String(id))?.name : null;
  return fromTable
    || f?.organization_name
    || f?.org_name
    || (id ? `Organisation ${id}` : "Unassigned organisation");
}

/**
 * Group the visible facilities into organisations.
 *
 * Keyed on organization_id where there is one and on the NAME otherwise, because an
 * unassigned facility has no id and two of them are not the same organisation just
 * because neither has one. Exported so the grouping can be tested without rendering.
 */
export function organisationsFrom(facilities = [], orgs = []) {
  const orgsById = new Map((orgs || []).filter((o) => o?.id).map((o) => [String(o.id), o]));
  const byOrg = new Map();
  for (const f of facilities) {
    // A facility with no ccn cannot be the one we call the route with, but it is still a
    // facility in the organisation and still gets restyled — so it is listed, and only
    // skipped when choosing the representative below.
    const key = orgIdOf(f) || `name:${orgNameFor(f, orgsById)}`;
    if (!byOrg.has(key)) byOrg.set(key, { key, id: orgIdOf(f), name: orgNameFor(f, orgsById), facilities: [] });
    byOrg.get(key).facilities.push(f);
  }
  return [...byOrg.values()]
    .map((o) => ({
      ...o,
      facilities: o.facilities.slice().sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    }))
    // Deterministic: the first facility with a ccn, by name, is the one the route is
    // called with. Any facility in the organisation resolves to the same organisation
    // server-side, so which one is arbitrary — but it must not be arbitrary per render.
    .map((o) => ({ ...o, representative: o.facilities.find((f) => ccnOf(f)) || null }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Turn a failed read into a sentence somebody can act on.
 *
 * The statuses are not interchangeable. 403 is the scope check refusing this facility —
 * which is the one a platform owner should never see and a VP legitimately can. 404 means
 * the API serving this browser does not have the theme route, which is a deploy, not a
 * permission. 401 is a stale session. Anything else is reported with its number rather
 * than hidden behind a general apology.
 */
export function describeReadFailure({ status, why } = {}, ccn) {
  const tail = why ? ` The server said: ${why}` : "";
  if (status === 403) {
    return `This session is not allowed to read facility ${ccn}, so the look cannot be shown for its organisation. `
      + `The facility-scope check runs before anything else on that route — a VP only reaches their own customer's `
      + `buildings.${tail}`;
  }
  if (status === 404) {
    // THIS SENTENCE USED TO NAME THE WRONG CAUSE, and that is worth a comment rather than
    // a quiet rewrite. It said "a deployment, not a permission — this browser is talking
    // to an API built before the route", which sent a reader to wait for a deploy that
    // would never have helped: the five theme calls were addressed to `/api/facilities/…`
    // while the engine router mounts at `/api/survai-engine`, and `server.js` has no
    // `/api/facilities` route at all. So the path had never existed on ANY build.
    //
    // A 404 from a client that is calling the wrong URL and a 404 from a server that has
    // not shipped the route look identical from here, and only one of them is something
    // the reader can wait out. So this now says which it cannot tell apart, and names the
    // check that settles it — rather than confidently naming the half that is cheaper to
    // believe.
    return `There is no theme route at that address for facility ${ccn} (404). Two different things look `
      + `exactly like this from the browser, and this screen cannot tell them apart: this build may be `
      + `addressing the wrong path (the engine's routes live under /api/survai-engine, and the API has no `
      + `/api/facilities routes of its own), or the API may be an older build that does not have the route `
      + `yet. Check the request URL in the network tab against /api/survai-engine/facilities/${ccn}/theme `
      + `before waiting on a deployment.${tail}`;
  }
  if (status === 401) {
    return `The session was not accepted (401). Sign out and in again, then reopen this screen.${tail}`;
  }
  return `The current look could not be read for facility ${ccn} (HTTP ${status ?? "no response"}).${tail}`;
}

/**
 * @param {object}   props
 * @param {object}   props.session
 * @param {Array}    props.facilities   the facilities this session can reach
 * @param {Array}    [props.orgs]       the organizations table, for their names
 * @param {string[]} props.ownerEmails  SYSTEM_ADMIN_EMAILS, passed in rather than imported
 * @param {Function} props.loadTheme    (ccn) => Promise<resolved|null>
 * @param {Function} props.saveTheme    (ccn, patch) => Promise<{ok, error?, resolved?}>
 */
export default function OrgLookSettings({ session, facilities = [], orgs: orgRows = [], ownerEmails = [], loadTheme, saveTheme }) {
  const allowed = canSetOrgLook(session, ownerEmails);
  const [orgKey, setOrgKey] = useState("");
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const orgs = useMemo(() => organisationsFrom(facilities, orgRows), [facilities, orgRows]);
  const org = useMemo(() => orgs.find((o) => o.key === orgKey) || null, [orgs, orgKey]);
  const ccn = org?.representative ? ccnOf(org.representative) : null;

  useEffect(() => {
    if (!allowed || !ccn) { setResolved(null); setLoadError(null); return undefined; }
    let cancelled = false;
    setResolved(null);
    setLoadError(null);
    (async () => {
      try {
        const answer = await loadTheme(ccn);
        if (cancelled) return;
        // NOT SWALLOWED, AND NOT COLLAPSED EITHER. ThemePicker's first paragraph and its
        // button are both built from this answer, so rendering against a failed read would
        // say "nothing is stored yet" for an organisation that has a look.
        //
        // The first version reported every failure with one sentence, which left the one
        // question that matters — WHY — unanswerable from the screen. The GET itself is
        // written never to fail (readThemeForFacility degrades for every reason it has and
        // puts the reason in `why`), so a refusal happened BEFORE the handler: the scope
        // check, authentication, or a route this deployment does not have. Those are four
        // different problems with four different fixes, and the status tells them apart.
        if (answer && answer.ok === false) {
          setLoadError(describeReadFailure(answer, ccn));
        } else if (!answer) {
          setLoadError("The current look could not be read for this organisation, and the attempt reported nothing. Try again, or open a facility's own detail panel.");
        } else {
          setResolved(answer.data ?? answer);
        }
      } catch (e) {
        if (!cancelled) setLoadError(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [allowed, ccn, loadTheme]);

  if (!allowed) return null;

  const onSave = async (patch) => {
    setBusy(true);
    try {
      const res = await saveTheme(ccn, patch);
      if (res?.ok && res.resolved) setResolved(res.resolved);
      return res;
    } finally {
      setBusy(false);
    }
  };

  const known = org ? org.facilities.length : 0;
  const serverCount = Number.isFinite(resolved?.facilities) ? resolved.facilities : null;
  const unseen = serverCount !== null ? serverCount - known : 0;

  const lbl = {
    fontSize: 12, fontWeight: 600, color: "var(--ed-ink-3)", display: "block", marginBottom: 5,
  };

  return (
    <div className="card" style={{ padding: 26, marginBottom: 18 }}>
      <div className="section-header">Organisation look</div>
      <div style={{ fontSize: 13, color: "var(--ed-muted)", marginBottom: 14, maxWidth: "68ch", lineHeight: 1.55 }}>
        Choose an organisation, then choose its theme. <strong>Every facility in that
        organisation gets the look</strong> — and no facility outside it is touched.
      </div>

      <label htmlFor="org-look-org" style={lbl}>Organisation</label>
      <select
        id="org-look-org"
        value={orgKey}
        onChange={(e) => setOrgKey(e.target.value)}
        style={{
          width: "100%", maxWidth: 420, border: "1.5px solid var(--ed-line)", borderRadius: 8,
          padding: "9px 12px", fontSize: 13, background: "var(--ed-canvas)", color: "var(--ed-ink)",
        }}
      >
        <option value="">Choose an organisation…</option>
        {orgs.map((o) => (
          <option key={o.key} value={o.key}>
            {o.name} · {o.facilities.length} {o.facilities.length === 1 ? "facility" : "facilities"}
          </option>
        ))}
      </select>

      {/* ── WHO THIS REACHES, BY NAME, BEFORE ANYTHING IS CHOSEN ─────────────
          The count is on the button already. The NAMES are what let somebody check
          they picked the chain they meant, which a number cannot do. */}
      {org ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ ...lbl, marginBottom: 6 }}>
            Facilities affected{serverCount !== null ? ` · ${serverCount}` : ""}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--ed-ink-2)", lineHeight: 1.6 }}>
            {org.facilities.map((f) => (
              <li key={ccnOf(f) || nameOf(f)}>
                {nameOf(f)}
                {ccnOf(f) ? <span style={{ color: "var(--ed-muted-2)" }}> · {ccnOf(f)}</span> : null}
              </li>
            ))}
          </ul>
          {/* THE TWO COUNTS DISAGREEING IS A FACT, NOT A ROUNDING ERROR. A VP scoped to
              part of a chain sees two names over a button that changes nine. */}
          {unseen > 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--ed-amber)", background: "var(--ed-amber-soft)", border: "1px solid #fcd34d", borderRadius: 8, padding: "9px 12px", margin: "10px 0 0", lineHeight: 1.5 }}>
              This organisation has <strong>{serverCount} facilities</strong> and you can see
              {" "}{known}. Saving changes the look for all {serverCount}, including the
              {" "}{unseen} not listed here.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* THREE STATES, NOT TWO: nothing chosen, chosen and read, chosen and the read
          failed. The third used to be indistinguishable from the first. */}
      {!orgKey ? (
        <p style={{ fontSize: 12.5, color: "var(--ed-muted-2)", margin: "12px 0 0" }}>
          {orgs.length
            ? "Nothing is loaded until an organisation is chosen, so none can be restyled by accident."
            : "No facilities are in scope for this session, so there is no organisation to restyle."}
        </p>
      ) : !ccn ? (
        <p style={{ fontSize: 12.5, color: "var(--ed-amber)", background: "var(--ed-amber-soft)", border: "1px solid #fcd34d", borderRadius: 8, padding: "9px 12px", margin: "12px 0 0", lineHeight: 1.5 }}>
          None of this organisation's facilities has a facility id, so its look cannot be set
          yet. Give one of them an id and this control will work.
        </p>
      ) : loadError ? (
        <p style={{ fontSize: 12.5, color: "var(--ed-amber)", background: "var(--ed-amber-soft)", border: "1px solid #fcd34d", borderRadius: 8, padding: "9px 12px", margin: "12px 0 0", lineHeight: 1.5 }}>
          {loadError}
        </p>
      ) : !resolved ? (
        <p style={{ fontSize: 12.5, color: "var(--ed-muted-2)", margin: "12px 0 0" }}>Reading the current look…</p>
      ) : (
        <ThemePicker
          ccn={ccn}
          orgName={org.name}
          resolved={resolved}
          onSave={onSave}
          busy={busy}
        />
      )}
    </div>
  );
}
